import type { UUID } from '@aline/common';
import { Apollo } from 'src/gql/generated';

export type AnswerOptionsUpsertInput = {
  options: Apollo.AnswerOptionProps[];
  id?: UUID;
};

export function withStableAnswerOptionIds(
  answerOptions: AnswerOptionsUpsertInput,
  serverOptions: Apollo.AnswerOptionProps[]
): AnswerOptionsUpsertInput {
  const stableIds = resolveStableServerOptionIds(answerOptions.options, serverOptions);

  return {
    ...answerOptions,
    options: answerOptions.options.map((option, index) => {
      const triggerAction = option.triggerAction ?? Apollo.TriggerAction.Merge;
      const serverId = stableIds[index];

      if (serverId) {
        return {
          ...option,
          id: serverId,
          triggerAction,
        };
      }

      return {
        optionText: option.optionText,
        triggerAction,
      };
    }),
  };
}

export function reconcileAnswerOptionsWithServer(
  localOptions: Apollo.AnswerOptionProps[],
  serverOptions: Apollo.AnswerOptionProps[]
): Apollo.AnswerOptionProps[] {
  const serverIds = collectServerIds(serverOptions);
  const matchedLocalServerOptionCount = localOptions.filter(
    option => option.id && serverIds.has(option.id)
  ).length;
  const unsavedLocalOptions = localOptions.filter(option => !option.id);
  const absorbedUnsavedCount = Math.max(0, serverOptions.length - matchedLocalServerOptionCount);
  const unsavedLocalOptionsToKeep = unsavedLocalOptions.slice(absorbedUnsavedCount);

  return [...serverOptions, ...unsavedLocalOptionsToKeep];
}

function resolveStableServerOptionIds(
  localOptions: Apollo.AnswerOptionProps[],
  serverOptions: Apollo.AnswerOptionProps[]
): (UUID | undefined)[] {
  const serverIds = collectServerIds(serverOptions);
  const usedServerIds = new Set<UUID>();
  const resolvedIds = localOptions.map(option => {
    if (!option.id || !serverIds.has(option.id)) return undefined;

    usedServerIds.add(option.id);
    return option.id;
  });

  const onlyClientIds = localOptions.every(option => !option.id || !serverIds.has(option.id));
  let nextServerIndex = 0;

  for (let localIndex = 0; localIndex < localOptions.length; localIndex++) {
    if (resolvedIds[localIndex]) continue;

    const localOption = localOptions[localIndex];
    const textMatch = claimNextServerOption({
      localOption,
      nextServerIndex,
      serverOptions,
      usedServerIds,
    });

    if (textMatch) {
      resolvedIds[localIndex] = textMatch.id;
      nextServerIndex = textMatch.nextServerIndex;
      continue;
    }

    if (!onlyClientIds) continue;

    const orderMatch = claimNextServerOption({
      nextServerIndex,
      serverOptions,
      usedServerIds,
    });

    if (!orderMatch) continue;

    resolvedIds[localIndex] = orderMatch.id;
    nextServerIndex = orderMatch.nextServerIndex;
  }

  return resolvedIds;
}

function collectServerIds(serverOptions: Apollo.AnswerOptionProps[]): Set<UUID> {
  return new Set(serverOptions.flatMap(option => (option.id ? [option.id] : [])));
}

function claimNextServerOption({
  localOption,
  nextServerIndex,
  serverOptions,
  usedServerIds,
}: {
  nextServerIndex: number;
  serverOptions: Apollo.AnswerOptionProps[];
  usedServerIds: Set<UUID>;
  localOption?: Apollo.AnswerOptionProps;
}): {
  id: UUID;
  nextServerIndex: number;
} | null {
  for (let index = nextServerIndex; index < serverOptions.length; index++) {
    const serverOption = serverOptions[index];
    const serverId = serverOption?.id;
    if (!serverId || usedServerIds.has(serverId)) continue;
    if (localOption && serverOption.optionText !== localOption.optionText) continue;

    usedServerIds.add(serverId);
    return { id: serverId, nextServerIndex: index + 1 };
  }

  return null;
}
